'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { issueHumanPass } from '@/actions';
import { logger } from '@/utils';

const SCRIPT_ID = 'cf-turnstile-script';
const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

type TurnstileApi = {
  render: (container: HTMLElement, options: Record<string, unknown>) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

export type HumanPassStatus = 'idle' | 'loading' | 'ready' | 'error';

export type UseHumanPassGateReturn = {
  containerRef: React.RefObject<HTMLDivElement | null>;
  status: HumanPassStatus;
  /** Resolve um novo desafio. Usar quando a cota do passe se esgota. */
  refresh: () => void;
  isConfigured: boolean;
  /**
   * O Cloudflare decidiu pedir interação e desenhou o desafio.
   *
   * Em `interaction-only` o widget fica com tamanho zero enquanto resolve
   * sozinho — que é o caso comum. Ganhar altura é o único sinal de que existe
   * algo para o usuário clicar, e é o que autoriza tirá-lo do canto e trazê-lo
   * para o centro da tela.
   */
  needsInteraction: boolean;
};

let scriptPromise: Promise<void> | null = null;

const loadScript = (): Promise<void> => {
  if (typeof window === 'undefined' || window.turnstile) {
    return Promise.resolve();
  }

  if (scriptPromise) {
    return scriptPromise;
  }

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);

    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('turnstile script')));
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('turnstile script'));
    document.head.appendChild(script);
  });

  return scriptPromise;
};

/**
 * Resolve um desafio Turnstile e troca o token por um passe de humanidade.
 *
 * O desafio roda na abertura do formulário, não no submit: a consulta de CPF
 * dispara enquanto o usuário digita, então o passe precisa já existir nesse
 * momento.
 */
export const useHumanPassGate = (): UseHumanPassGateReturn => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [status, setStatus] = useState<HumanPassStatus>('idle');
  const [needsInteraction, setNeedsInteraction] = useState(false);

  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

  useEffect(() => {
    if (!siteKey || !containerRef.current) {
      return;
    }

    let cancelled = false;
    setStatus('loading');

    loadScript()
      .then(() => {
        if (cancelled || !window.turnstile || !containerRef.current) {
          return;
        }

        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action: 'cpf-lookup',
          // Fixo em vez do 'auto' padrão, que seguiria o idioma do navegador:
          // o site inteiro é pt-BR, e um desafio em inglês no meio do checkout
          // parece coisa de terceiro e derruba confiança.
          language: 'pt-br',
          // Só aparece se o Cloudflare julgar que precisa de interação.
          appearance: 'interaction-only',
          callback: (token: string) => {
            void issueHumanPass(token)
              .then((result) => {
                if (!cancelled) {
                  setStatus(result.ok ? 'ready' : 'error');
                }
              })
              .catch((error) => {
                logger.error('Falha ao emitir passe de humanidade:', error);
                if (!cancelled) {
                  setStatus('error');
                }
              });
          },
          'error-callback': () => {
            if (!cancelled) {
              setStatus('error');
            }
          },
          'expired-callback': () => {
            if (widgetIdRef.current) {
              window.turnstile?.reset(widgetIdRef.current);
            }
          },
        });
      })
      .catch((error) => {
        logger.error('Falha ao carregar o Turnstile:', error);
        if (!cancelled) {
          setStatus('error');
        }
      });

    return () => {
      cancelled = true;

      if (widgetIdRef.current) {
        window.turnstile?.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [siteKey]);

  // Observa o tamanho do container: o Turnstile não avisa que passou a exigir
  // interação, mas desenhar o desafio muda a altura de zero para algo.
  useEffect(() => {
    const container = containerRef.current;

    if (!siteKey || !container || typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];

      if (!entry) {
        return;
      }

      const { height, width } = entry.contentRect;
      setNeedsInteraction(height > 0 && width > 0);
    });

    observer.observe(container);

    return () => observer.disconnect();
  }, [siteKey]);

  const refresh = useCallback(() => {
    if (!widgetIdRef.current) {
      return;
    }

    setStatus('loading');

    // O container só volta a ser exibido no render que este setStatus dispara.
    // Resetar aqui desenharia um desafio interativo dentro de um elemento
    // ainda escondido, e o usuário não teria como resolvê-lo.
    requestAnimationFrame(() => {
      if (widgetIdRef.current) {
        window.turnstile?.reset(widgetIdRef.current);
      }
    });
  }, []);

  return {
    containerRef,
    status,
    refresh,
    isConfigured: Boolean(siteKey),
    needsInteraction,
  };
};
